/**
 * API xác thực danh tính phía học viên (TT 83 C1-c/C1-d).
 * Mirror BE `/api/identity/*` — xem `backend/apps/identity/views.py`.
 */
import { apiClient } from "@/api/client";

export type EkycStatus = "none" | "passed" | "failed" | "needs_review";

export interface IdentityStatus {
  consent: { granted: boolean; policy_version: string };
  ekyc: { status: EkycStatus; verified_at: string | null };
  required_for_exam: boolean;
}

export interface VerificationResult {
  id: number;
  kind: "enrollment_ekyc" | "exam_face_match";
  status: "passed" | "failed" | "needs_review";
  reason: string;
}

/** Mã lỗi BE trả khi chặn vào thi (AttemptStartView) hoặc khi xác thực. */
export type IdentityGateCode =
  | "ekyc_required"
  | "face_match_required"
  | "face_match_pending"
  | "face_match_failed";

export const IDENTITY_GATE_CODES: IdentityGateCode[] = [
  "ekyc_required",
  "face_match_required",
  "face_match_pending",
  "face_match_failed",
];

export async function getIdentityStatus(): Promise<IdentityStatus> {
  return (await apiClient.get<IdentityStatus>("/identity/status/")).data;
}

export async function grantIdentityConsent(policyVersion: string): Promise<void> {
  await apiClient.post("/identity/consent/", { policy_version: policyVersion });
}

export async function revokeIdentityConsent(): Promise<{ deleted_images: number }> {
  return (await apiClient.delete<{ deleted_images: number }>("/identity/consent/")).data;
}

export async function submitEkyc(files: {
  id_front: File;
  id_back: File;
  selfie: File;
}): Promise<VerificationResult> {
  const form = new FormData();
  form.append("id_front", files.id_front);
  form.append("id_back", files.id_back);
  form.append("selfie", files.selfie);
  return (await apiClient.post<VerificationResult>("/identity/ekyc/", form)).data;
}

export async function submitExamFaceMatch(quizId: number, selfie: File): Promise<VerificationResult> {
  const form = new FormData();
  form.append("quiz_id", String(quizId));
  form.append("selfie", selfie);
  return (await apiClient.post<VerificationResult>("/identity/exam-face-match/", form)).data;
}

/** Lấy `code` từ lỗi axios (403/429...) — undefined nếu không có. */
export function errorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
}

export function errorDetail(err: unknown, fallback: string): string {
  return (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback;
}
